<div class="ds-roles">
  <div class="ds-role-select-row">
    <div class="ds-role-select-field">
      <label for="ds-role-select">Role</label>
      @if (rolesLoading || catalogsLoading) {
        <div class="ds-roles-loading"><span class="spinner"></span> Loading roles...</div>
      } @else if (roles.length === 0) {
        <p class="ds-empty">No roles yet for this tenant.</p>
      } @else {
        <select
          id="ds-role-select"
          class="ds-role-select"
          [ngModel]="selectedRoleId"
          (ngModelChange)="selectRole($event)"
          [disabled]="catalogsLoading"
        >
          <option [ngValue]="null" disabled>Select a role...</option>
          @for (role of roles; track role.id) {
            <option [ngValue]="role.id">{{ role.name }}</option>
          }
        </select>
      }
    </div>
    <button type="button" class="add-role-btn" [disabled]="catalogsLoading" (click)="openAddRole()">
      <i class="bi bi-plus-lg"></i> Add Role
    </button>
  </div>

  @if (!selectedRoleId) {
    <div class="ds-placeholder">
      <i class="bi bi-hand-index-thumb"></i>
      <p>Select a role above to manage its permissions.</p>
    </div>
  } @else {
    <div class="ds-role-card">
      <div class="ds-role-header">
        <div class="ds-role-header-text">
          <span class="ds-role-header-avatar">{{ selectedRole?.name?.charAt(0) }}</span>
          <div>
            <h3>{{ selectedRole?.name }}</h3>
            @if (selectedRole?.description) {
              <p>{{ selectedRole!.description }}</p>
            }
          </div>
        </div>
        <div class="ds-role-header-actions">
          <button type="button" class="role-action-btn" (click)="openEditRole()">
            <i class="bi bi-pencil"></i> Edit
          </button>
          <button type="button" class="role-action-btn danger" (click)="openDeleteRole()">
            <i class="bi bi-trash"></i> Delete
          </button>
        </div>
      </div>

      <p class="ds-sections-hint">Each section below is saved independently — update and save schemas, containers, or pages on their own.</p>

      <div class="ds-groups">
        <!-- Schemas: has the extra table-level scope layer -->
        <div class="ds-group-card">
          <div class="ds-group-header">
            <div class="ds-group-heading">
              <span class="ds-group-icon"><i class="bi bi-diagram-3"></i></span>
              <div class="ds-group-heading-text">
                <span class="ds-group-title">Schemas</span>
                <span class="ds-group-subtitle">
                  @if (!loading.schemas) { {{ schemas.length }} schema{{ schemas.length === 1 ? '' : 's' }} available }
                </span>
              </div>
            </div>
            <div class="ds-group-actions">
              <label class="select-all-label">
                <input
                  type="checkbox"
                  [checked]="isAllSelected('schemas')"
                  [indeterminate]="isIndeterminate('schemas')"
                  (change)="toggleSelectAll('schemas')"
                />
                Select all
              </label>
              <button type="button" class="group-save-btn" [disabled]="saving.schemas" (click)="saveGroup('schemas')">
                @if (saving.schemas) { <span class="spinner"></span> Saving... } @else { <i class="bi bi-check2"></i> Save }
              </button>
            </div>
          </div>
          <div class="ds-group-body">
            @if (loading.schemas) {
              <div class="ds-group-loading"><span class="spinner"></span> Loading schemas...</div>
            } @else if (schemas.length === 0) {
              <p class="ds-empty">No schemas available.</p>
            } @else {
              <div class="ds-schema-list">
                @for (schema of schemas; track schema.name) {
                  <div class="ds-schema-row" [class.is-selected]="schema.selected">
                    <label class="item-checkbox">
                      <input type="checkbox" [(ngModel)]="schema.selected" />
                      <span>{{ schema.name }}</span>
                    </label>
                    <span class="scope-badge">{{ schemaBadge(schema) }}</span>
                    @if (schema.availableTables.length > 0) {
                      <button type="button" class="configure-btn" title="Configure tables" (click)="openPicker(schema)">
                        <i class="bi bi-sliders"></i>
                      </button>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <!-- Containers & Pages: identical flat checklist shape -->
        @for (group of simpleGroups; track group.key) {
          <div class="ds-group-card">
            <div class="ds-group-header">
              <div class="ds-group-heading">
                <span class="ds-group-icon"><i class="bi" [class]="group.icon"></i></span>
                <div class="ds-group-heading-text">
                  <span class="ds-group-title">{{ group.title }}</span>
                  <span class="ds-group-subtitle">
                    @if (!loading[group.key]) { {{ itemsFor(group.key).length }} available }
                  </span>
                </div>
              </div>
              <div class="ds-group-actions">
                <label class="select-all-label">
                  <input
                    type="checkbox"
                    [checked]="isAllSelected(group.key)"
                    [indeterminate]="isIndeterminate(group.key)"
                    (change)="toggleSelectAll(group.key)"
                  />
                  Select all
                </label>
                <button type="button" class="group-save-btn" [disabled]="saving[group.key]" (click)="saveGroup(group.key)">
                  @if (saving[group.key]) { <span class="spinner"></span> Saving... } @else { <i class="bi bi-check2"></i> Save }
                </button>
              </div>
            </div>
            <div class="ds-group-body">
              @if (loading[group.key]) {
                <div class="ds-group-loading"><span class="spinner"></span> Loading {{ group.title.toLowerCase() }}...</div>
              } @else if (itemsFor(group.key).length === 0) {
                <p class="ds-empty">No {{ group.title.toLowerCase() }} available.</p>
              } @else {
                <div class="ds-chip-grid">
                  @for (item of itemsFor(group.key); track item.name) {
                    <label class="item-checkbox chip" [class.is-selected]="item.selected">
                      <input type="checkbox" [(ngModel)]="item.selected" />
                      <span>{{ item.name }}</span>
                    </label>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>

<!-- Schema table-level picker: draft/apply/cancel, scoped to one schema -->
@if (activeSchema) {
  <app-tenant-role-wizard [title]="'Configure tables — ' + activeSchema.name" size="sm" (closed)="cancelPicker()">
    <div class="picker-search">
      <i class="bi bi-search"></i>
      <input type="text" [(ngModel)]="pickerSearch" placeholder="Search tables..." />
    </div>
    <label class="item-checkbox select-all-row">
      <input type="checkbox" [checked]="isPickerAllSelected()" (change)="togglePickerSelectAll()" />
      Select all
    </label>
    <div class="picker-list">
      @for (table of filteredPickerTables(); track table) {
        <label class="item-checkbox">
          <input type="checkbox" [checked]="isPickerTableSelected(table)" (change)="togglePickerTable(table)" />
          {{ table }}
        </label>
      } @empty {
        <p class="ds-empty">No tables found.</p>
      }
    </div>
    <div footer>
      <button type="button" class="cancel-btn" (click)="cancelPicker()">Cancel</button>
      <button type="button" class="apply-btn" (click)="applyPicker()">Apply</button>
    </div>
  </app-tenant-role-wizard>
}

<!-- Add Role dialog -->
@if (isAddRoleOpen) {
  <app-tenant-role-wizard title="Create Role" size="lg" (closed)="closeAddRole()">
    @if (creatingRole) {
      <app-loader />
    }

    <div class="new-role-form">
      <div class="form-row">
        <label>Name</label>
        <input type="text" [(ngModel)]="newRoleName" placeholder="e.g. Support Agent" />
      </div>
      <div class="form-row">
        <label>Description</label>
        <input type="text" [(ngModel)]="newRoleDescription" placeholder="What is this role for?" />
      </div>
    </div>

    <div class="ds-groups new-role-groups">
      <div class="ds-group-card">
        <div class="ds-group-header">
          <label class="select-all-label">
            <input
              type="checkbox"
              [checked]="isListAllSelected(newRoleSchemas)"
              (change)="toggleListSelectAll(newRoleSchemas)"
            />
            <i class="bi bi-diagram-3"></i> Schemas
          </label>
        </div>
        <div class="ds-group-body">
          @for (schema of newRoleSchemas; track schema.name) {
            <div class="ds-schema-row">
              <label class="item-checkbox">
                <input type="checkbox" [(ngModel)]="schema.selected" />
                {{ schema.name }}
              </label>
              <span class="scope-badge">{{ schemaBadge(schema) }}</span>
              @if (schema.availableTables.length > 0) {
                <button type="button" class="configure-btn" (click)="openPicker(schema)">
                  <i class="bi bi-sliders"></i>
                </button>
              }
            </div>
          }
        </div>
      </div>

      <div class="ds-group-card">
        <div class="ds-group-header">
          <label class="select-all-label">
            <input
              type="checkbox"
              [checked]="isListAllSelected(newRoleContainers)"
              (change)="toggleListSelectAll(newRoleContainers)"
            />
            <i class="bi bi-box-seam"></i> Containers
          </label>
        </div>
        <div class="ds-group-body">
          @for (item of newRoleContainers; track item.name) {
            <label class="item-checkbox">
              <input type="checkbox" [(ngModel)]="item.selected" />
              {{ item.name }}
            </label>
          }
        </div>
      </div>

      <div class="ds-group-card">
        <div class="ds-group-header">
          <label class="select-all-label">
            <input
              type="checkbox"
              [checked]="isListAllSelected(newRolePages)"
              (change)="toggleListSelectAll(newRolePages)"
            />
            <i class="bi bi-window"></i> Pages
          </label>
        </div>
        <div class="ds-group-body">
          @for (item of newRolePages; track item.name) {
            <label class="item-checkbox">
              <input type="checkbox" [(ngModel)]="item.selected" />
              {{ item.name }}
            </label>
          }
        </div>
      </div>
    </div>

    <div footer>
      <button type="button" class="cancel-btn" [disabled]="creatingRole" (click)="closeAddRole()">Cancel</button>
      <button type="button" class="apply-btn" [disabled]="!canSubmitNewRole || creatingRole" (click)="submitNewRole()">
        @if (creatingRole) { <span class="spinner"></span> Creating... } @else { Create Role }
      </button>
    </div>
  </app-tenant-role-wizard>
}

<!-- Edit Role dialog -->
@if (isEditRoleOpen) {
  <app-tenant-role-wizard title="Edit Role" size="sm" (closed)="closeEditRole()">
    <div class="new-role-form single-col">
      <div class="form-row">
        <label>Name</label>
        <input type="text" [(ngModel)]="editRoleName" placeholder="e.g. Support Agent" />
      </div>
      <div class="form-row">
        <label>Description</label>
        <input type="text" [(ngModel)]="editRoleDescription" placeholder="What is this role for?" />
      </div>
    </div>

    <div footer>
      <button type="button" class="cancel-btn" [disabled]="savingRoleEdit" (click)="closeEditRole()">Cancel</button>
      <button type="button" class="apply-btn" [disabled]="!canSubmitRoleEdit || savingRoleEdit" (click)="submitRoleEdit()">
        @if (savingRoleEdit) { <span class="spinner"></span> Saving... } @else { Save Changes }
      </button>
    </div>
  </app-tenant-role-wizard>
}

<!-- Delete Role confirm -->
@if (isDeleteRoleOpen) {
  <app-tenant-role-wizard title="Remove Role" size="sm" (closed)="closeDeleteRole()">
    <p class="confirm-text">
      You're about to remove <strong>{{ selectedRole?.name }}</strong>. Its permissions and page
      assignments will be deleted, and any users on this role will lose it. This can't be undone.
    </p>

    <div footer>
      <button type="button" class="cancel-btn" [disabled]="deletingRole" (click)="closeDeleteRole()">Cancel</button>
      <button type="button" class="danger-btn" [disabled]="deletingRole" (click)="confirmDeleteRole()">
        @if (deletingRole) { <span class="spinner"></span> Removing... } @else { Remove Role }
      </button>
    </div>
  </app-tenant-role-wizard>
}
